<template>
  <v-dialog v-model="dialogModel" :fullscreen="$vuetify.breakpoint.smAndDown" max-width="1400" scrollable>
    <v-card data-testid="client-duplicates-dialog">
      <v-card-title class="d-flex align-center">
        <v-icon left>mdi-account-multiple-check</v-icon>
        {{ $t("client_duplicates.title") }}
        <v-spacer></v-spacer>
        <v-btn icon :aria-label="$t('client_duplicates.close')" @click="dialogModel = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-4">
        <div v-if="loading" class="d-flex justify-center py-10">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <v-alert v-else-if="groups.length === 0" type="info" text class="mb-0" data-testid="duplicates-empty">
          {{ $t("client_duplicates.no_duplicates") }}
        </v-alert>

        <template v-else>
          <div class="text-subtitle-1 font-weight-bold mb-2">{{ $t("client_duplicates.groups") }}</div>
          <v-row dense>
            <v-col v-for="group in groups" :key="group.key" cols="12" sm="6" md="4">
              <v-card
                outlined
                class="duplicate-group pa-3"
                :class="{ 'duplicate-group--selected': selectedGroup && selectedGroup.key === group.key }"
                tabindex="0"
                @click="selectGroup(group)"
                @keydown.enter="selectGroup(group)"
              >
                <div class="d-flex align-center flex-wrap" style="gap: 6px">
                  <strong>{{ $t("client_duplicates.group_size", { count: group.clients.length }) }}</strong>
                  <v-chip v-if="group.flags_diverge" x-small color="warning" text-color="black">
                    {{ $t("client_duplicates.flags_diverge") }}
                  </v-chip>
                </div>
                <div v-if="names(group)" class="text-body-2 mt-1 text-truncate" :title="names(group)">
                  {{ names(group) }}
                </div>
                <div class="text-caption mt-1">
                  {{ $t("client_duplicates.match_reason", { reason: matchReason(group) }) }}
                </div>
                <div v-for="pair in dismissedPairs(group)" :key="pair.join('-')" class="text-caption error--text mt-1">
                  {{ $t("client_duplicates.dismissed_pair", { a: `#${pair[0]}`, b: `#${pair[1]}` }) }}
                </div>
              </v-card>
            </v-col>
          </v-row>

          <div v-if="pageCount > 1" class="d-flex justify-center my-3">
            <v-pagination v-model="page" :length="pageCount" :total-visible="7" @input="loadGroups"></v-pagination>
          </div>

          <template v-if="selectedGroup">
            <v-divider class="my-4"></v-divider>

            <v-radio-group v-model="selection.mainId" class="mt-0" hide-details @change="onMainChange">
              <v-row dense align="stretch">
                <v-col v-for="client in selectedGroup.clients" :key="client.id" cols="12" md="4">
                  <v-card outlined height="100%" class="duplicate-client-card">
                    <v-card-title class="text-subtitle-1 py-2">
                      <span>#{{ client.id }} · {{ displayName(client) || "—" }}</span>
                    </v-card-title>
                    <v-card-text class="pb-2">
                      <div class="d-flex align-center mb-2" style="gap: 12px">
                        <v-radio
                          :value="client.id"
                          :label="$t('client_duplicates.main')"
                          hide-details
                          dense
                          class="mt-0 pt-0 mb-0"
                        ></v-radio>
                        <v-checkbox
                          v-model="selection.selectedIds"
                          :value="client.id"
                          :disabled="selection.mainId === client.id"
                          :label="$t('client_duplicates.include')"
                          hide-details
                          dense
                          class="mt-0 pt-0"
                          @change="onSelectionChange"
                        ></v-checkbox>
                      </div>

                      <dl class="duplicate-client-fields mb-0">
                        <template v-for="field in clientFields(client)">
                          <dt :key="`${field.key}-label`">{{ field.label }}</dt>
                          <dd :key="field.key">{{ field.value }}</dd>
                        </template>
                      </dl>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-radio-group>

            <div v-if="previewLoading" class="d-flex justify-center py-6">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>

            <v-row v-else-if="preview" dense class="mt-2">
              <v-col cols="12" md="8">
                <v-card outlined>
                  <v-card-title class="text-subtitle-1">{{ $t("client_duplicates.after_merge") }}</v-card-title>
                  <v-card-text>
                    <v-simple-table dense>
                      <tbody>
                        <tr v-for="entry in resultEntries" :key="entry.key">
                          <th scope="row">{{ entry.label }}</th>
                          <td class="duplicate-result-value" :class="{ 'font-weight-bold primary--text': entry.changed }">{{ entry.value }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="4">
                <v-card outlined height="100%">
                  <v-card-title class="text-subtitle-1">
                    {{ $t("client_duplicates.moved_records", { count: movedTotal }) }}
                  </v-card-title>
                  <v-card-text>
                    <div v-for="entry in movedEntries" :key="entry.key" class="d-flex justify-space-between py-1">
                      <span>{{ entry.label }}</span><strong class="ml-2">{{ entry.value }}</strong>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <v-alert v-if="blockers.length" type="error" text class="mt-4" data-testid="duplicates-blockers">
              <div class="font-weight-bold mb-1">{{ $t("client_duplicates.blockers_title") }}</div>
              <div v-for="(blocker, index) in blockers" :key="`${blocker.code}-${index}`">
                {{ blockerMessage(blocker) }}
                <span v-if="blocker.client_ids && blocker.client_ids.length"> (#{{ blocker.client_ids.join(", #") }})</span>
              </div>
            </v-alert>
          </template>
        </template>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="flex-wrap" style="gap: 8px">
        <v-btn
          v-if="selectedGroup"
          text
          color="warning"
          :loading="dismissLoading"
          :disabled="mergeLoading || previewLoading || dismissLoading"
          @click="dismissGroup"
        >
          {{ $t("client_duplicates.dismiss") }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="dialogModel = false">{{ $t("client_duplicates.close") }}</v-btn>
        <v-btn
          v-if="selectedGroup"
          color="primary"
          :loading="mergeLoading"
          :disabled="!mergeAllowed"
          data-testid="duplicates-merge"
          @click="mergeGroup"
        >
          {{ $t("client_duplicates.merge") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  blockerText,
  buildPreviewPayload,
  buildResultEntries,
  buildMovedEntries,
  canMergeSelection,
  categoryLabel,
  changeMain,
  clientDisplayName,
  clientNotesLabel,
  createDismissIntent,
  createMergeIntent,
  createSelection,
  errorCodeFromResponse,
  groupNames,
  pageToReloadAfterRemoval,
} from "@/components/dashboard/clients/clientDuplicates";

const DEFAULT_PAGE_SIZE = 20;
const MATCH_REASON_KEYS = Object.freeze({
  phone: "client_duplicates.matched_by.phone",
  passport: "client_duplicates.matched_by.passport",
  email: "client_duplicates.matched_by.email",
});
const FIELD_KEYS = Object.freeze({
  fio: "client_duplicates.fields.fio",
  phone: "client_duplicates.fields.phone",
  email: "client_duplicates.fields.email",
  passport: "client_duplicates.fields.passport",
  driver_license: "client_duplicates.fields.driver_license",
  branch: "client_duplicates.fields.branch",
  created_at: "client_duplicates.fields.created_at",
  bookings: "client_duplicates.fields.bookings",
  last_booking_at: "client_duplicates.fields.last_booking_at",
  balance: "client_duplicates.fields.balance",
  category: "client_duplicates.fields.category",
  discounts: "client_duplicates.fields.discounts",
  notes: "client_duplicates.fields.notes",
});

export default {
  name: "ClientDuplicatesDialog",
  props: {
    value: { type: Boolean, default: false },
    branches: { type: Array, default: () => [] },
  },
  data() {
    return {
      groups: [],
      totalGroups: 0,
      page: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      selectedGroupKey: null,
      selection: { mainId: null, selectedIds: [] },
      preview: null,
      blockers: [],
      loading: false,
      previewLoading: false,
      previewReady: false,
      mergeLoading: false,
      dismissLoading: false,
      previewRequestId: 0,
      loadRequestId: 0,
    };
  },
  computed: {
    dialogModel: {
      get() { return this.value; },
      set(value) { this.$emit("input", value); },
    },
    selectedGroup() {
      return this.groups.find((group) => group.key === this.selectedGroupKey) || null;
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.totalGroups / this.pageSize));
    },
    mergeAllowed() {
      return canMergeSelection({
        selection: this.selection,
        group: this.selectedGroup,
        blockers: this.blockers,
        previewPending: this.previewLoading || this.mergeLoading || this.dismissLoading,
        previewReady: this.previewReady,
      });
    },
    resultEntries() {
      const main = this.selectedGroup && this.selectedGroup.clients.find((client) => client.id === this.selection.mainId);
      return buildResultEntries(this.preview && this.preview.result, main, this.$t.bind(this));
    },
    movedEntries() {
      return buildMovedEntries(this.preview && this.preview.moved, this.$t.bind(this));
    },
    movedTotal() {
      return this.movedEntries.reduce((sum, entry) => sum + (Number(entry.value) || 0), 0);
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.page = 1;
        this.loadGroups();
      } else {
        this.previewRequestId += 1;
        this.loadRequestId += 1;
        this.previewLoading = false;
        this.loading = false;
      }
    },
  },
  methods: {
    requestConfig() {
      return { headers: { Authorization: this.$store.getters.getAuthToken } };
    },
    async loadGroups() {
      const requestId = ++this.loadRequestId;
      this.loading = true;
      this.groups = [];
      this.totalGroups = 0;
      this.clearSelection();
      try {
        const response = await this.axios.get("/api/v1/client_duplicates", {
          ...this.requestConfig(),
          params: { page: this.page },
        });
        if (requestId !== this.loadRequestId) return;
        this.groups = response.data.groups || [];
        this.totalGroups = Number(response.data.total_groups) || 0;
        if (this.page === 1 && this.groups.length) this.pageSize = this.groups.length;

        if (this.groups.length) this.selectGroup(this.groups[0]);
        else this.clearSelection();
      } catch (error) {
        if (requestId !== this.loadRequestId) return;
        this.groups = [];
        this.totalGroups = 0;
        this.clearSelection();
        this.notifyError(this.$t("client_duplicates.errors.load"));
      } finally {
        if (requestId === this.loadRequestId) this.loading = false;
      }
    },
    selectGroup(group) {
      if (!group) return;
      this.selectedGroupKey = group.key;
      this.selection = createSelection(group);
      this.preview = null;
      this.blockers = [];
      this.previewReady = false;
      this.requestPreview();
    },
    clearSelection() {
      this.previewRequestId += 1;
      this.selectedGroupKey = null;
      this.selection = { mainId: null, selectedIds: [] };
      this.preview = null;
      this.blockers = [];
      this.previewReady = false;
      this.previewLoading = false;
    },
    onMainChange(mainId) {
      this.selection = changeMain(this.selection, mainId);
      this.requestPreview();
    },
    onSelectionChange() {
      if (!this.selection.selectedIds.includes(this.selection.mainId)) {
        this.selection = changeMain(this.selection, this.selection.mainId);
      }
      this.requestPreview();
    },
    async requestPreview() {
      if (!this.selectedGroup || this.selection.mainId === null) return;
      const requestId = ++this.previewRequestId;
      this.previewLoading = true;
      this.previewReady = false;
      this.preview = null;
      this.blockers = [];
      try {
        const response = await this.axios.post(
          "/api/v1/client_duplicates/preview",
          buildPreviewPayload(this.selection, this.selectedGroup),
          this.requestConfig()
        );
        if (requestId !== this.previewRequestId) return;
        this.preview = response.data;
        this.blockers = response.data.blockers || [];
        this.previewReady = true;
      } catch (error) {
        if (requestId !== this.previewRequestId) return;
        const responseBlockers = error.response && error.response.data && error.response.data.blockers;
        if (Array.isArray(responseBlockers)) this.blockers = responseBlockers;
        this.notifyError(this.$t("client_duplicates.errors.preview"));
      } finally {
        if (requestId === this.previewRequestId) this.previewLoading = false;
      }
    },
    async mergeGroup() {
      if (!this.mergeAllowed) return;
      const intent = createMergeIntent(this.selection, this.selectedGroup);
      const confirmed = await this.$confirm({
        title: this.$t("client_duplicates.confirm_title"),
        text: this.$t("client_duplicates.confirm_text", {
          cards: intent.cardsCount,
          bookings: intent.bookingsCount,
        }),
        icon: "mdi-alert-circle-outline",
        confirmButtonText: this.$t("client_duplicates.confirm_button"),
        cancelButtonText: this.$t("client_duplicates.cancel_button"),
      });
      if (!confirmed) return;

      this.mergeLoading = true;
      try {
        await this.axios.post(
          "/api/v1/client_duplicates/merge",
          intent.payload,
          this.requestConfig()
        );
        this.$notify(this.$t("client_duplicates.merged"), "", "success");
        this.removeSelectedGroup(intent.groupKey);
        this.$emit("merged");
      } catch (error) {
        this.handleMergeError(error);
      } finally {
        this.mergeLoading = false;
      }
    },
    async dismissGroup() {
      if (!this.selectedGroup || this.dismissLoading) return;
      const intent = createDismissIntent(this.selectedGroup);
      this.dismissLoading = true;
      try {
        await this.axios.post(
          "/api/v1/client_duplicates/dismiss",
          intent.payload,
          this.requestConfig()
        );
        this.$notify(this.$t("client_duplicates.dismissed"), "", "success");
        this.removeSelectedGroup(intent.groupKey);
      } catch (error) {
        this.notifyError(this.$t("client_duplicates.errors.dismiss"));
      } finally {
        this.dismissLoading = false;
      }
    },
    handleMergeError(error) {
      const status = error.response && error.response.status;
      const data = (error.response && error.response.data) || {};
      const code = errorCodeFromResponse(data);

      if (status === 409 || code === "locked") {
        this.notifyError(this.$t("client_duplicates.errors.locked"));
        return;
      }
      if (status === 422 && code === "already_merged") {
        this.notifyError(this.$t("client_duplicates.errors.already_merged"));
        return;
      }
      if (status === 422 && code === "stale") {
        this.notifyError(this.$t("client_duplicates.errors.stale"));
        return;
      }
      if (status === 422 && Array.isArray(data.blockers)) {
        this.preview = null;
        this.blockers = data.blockers;
        this.previewReady = false;
        this.notifyError(this.$t("client_duplicates.errors.blocked"));
        return;
      }
      this.notifyError(this.$t("client_duplicates.errors.merge"));
    },
    removeSelectedGroup(groupKey = this.selectedGroupKey) {
      const index = this.groups.findIndex((group) => group.key === groupKey);
      if (index < 0) {
        this.loadGroups();
        return;
      }
      const wasSelected = groupKey === this.selectedGroupKey;
      this.groups.splice(index, 1);
      this.totalGroups = Math.max(0, this.totalGroups - 1);
      if (!wasSelected) return;
      const next = this.groups[Math.min(index, this.groups.length - 1)];
      if (next) this.selectGroup(next);
      else {
        this.clearSelection();
        const pageToReload = pageToReloadAfterRemoval({
          page: this.page,
          totalGroups: this.totalGroups,
          pageSize: this.pageSize,
          remainingOnPage: this.groups.length,
        });
        if (pageToReload !== null) {
          this.page = pageToReload;
          this.loadGroups();
        }
      }
    },
    notifyError(message) {
      this.$notify(this.$t("client_duplicates.errors.title"), message, "error", 5000);
    },
    matchReason(group) {
      const reasons = [];
      (group.clients || []).forEach((client) => {
        (client.matched_by || []).forEach((reason) => {
          if (!reasons.includes(reason)) reasons.push(reason);
        });
      });
      return reasons.map((reason) => this.$t(MATCH_REASON_KEYS[reason] || "client_duplicates.matched_by.other")).join(", ") || "—";
    },
    displayName(client) {
      return clientDisplayName(client);
    },
    names(group) {
      return groupNames(group);
    },
    dismissedPairs(group) {
      return group.dismissed_pairs || [];
    },
    clientFields(client) {
      return [
        ["fio", clientDisplayName(client)],
        ["phone", client.phone],
        ["email", client.email],
        ["passport", client.passport],
        ["driver_license", client.driver_license],
        ["branch", this.branchLabel(client.company_id)],
        ["created_at", this.formatDate(client.created_at)],
        ["bookings", client.bookings_count],
        ["last_booking_at", this.formatDate(client.last_booking_at)],
        ["balance", client.balance],
        ["category", categoryLabel(client.category, this.$t.bind(this))],
        ["discounts", [client.sale, client.sale_cash].filter((value) => value !== null && value !== undefined && value !== "").join(" / ")],
        ["notes", this.notesLabel(client)],
      ].map(([key, value]) => ({
        key,
        label: this.$t(FIELD_KEYS[key]),
        value: value === null || value === undefined || value === "" ? "—" : value,
      }));
    },
    branchLabel(companyId) {
      const branch = this.branches.find((item) => item.id === companyId);
      if (!branch) return companyId;
      return [branch.name, branch.city].filter(Boolean).join(" · ") || branch.id;
    },
    notesLabel(client) {
      return clientNotesLabel(client, this.$t.bind(this));
    },
    formatDate(value) {
      if (!value) return "—";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return new Intl.DateTimeFormat((this.$i18n.locale || "ru").replace("_", "-"), {
        dateStyle: "short",
        timeStyle: "short",
      }).format(date);
    },
    blockerMessage(blocker) {
      return blockerText(blocker, this.$t.bind(this));
    },
  },
};
</script>

<style scoped>
.duplicate-group {
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}
.duplicate-group--selected {
  border-color: var(--v-primary-base) !important;
  background: rgba(25, 118, 210, 0.08);
}
.duplicate-client-card {
  min-width: 0;
}
.duplicate-result-value {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.duplicate-client-fields {
  display: grid;
  grid-template-columns: minmax(120px, 0.8fr) minmax(0, 1.2fr);
  gap: 6px 12px;
  overflow-wrap: anywhere;
}
.duplicate-client-fields dt {
  color: rgba(0, 0, 0, 0.6);
}
.theme--dark .duplicate-client-fields dt {
  color: rgba(255, 255, 255, 0.7);
}
.duplicate-client-fields dd {
  margin: 0;
}
@media (max-width: 599px) {
  .duplicate-client-fields {
    grid-template-columns: 1fr;
    gap: 2px;
  }
  .duplicate-client-fields dd {
    margin-bottom: 8px;
  }
}
</style>
